import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  AlertCircle, ExternalLink, Loader2,
  Download, Mail, Radio, Zap, Lock, Users, Building2,
  ChevronRight, Sparkles, Share2, RefreshCw
} from 'lucide-react';
import { apiClient } from '../../lib/api';
import { ProfessionalTable } from '../dashboard/ProfessionalTable';
import { getAppUrl } from '../../utils/validation';

import { DatasetPromoModal, isDatasetPromoDismissed } from './DatasetPromoModal';
import { escapeHtml, escapeRegex } from '../../utils/sanitization';
import { sidebarColors } from '../../styles/tokens';
import skylarqLogo from '../../assets/skylarq_logo_small.png';

interface SharedDataset {
  dataset: {
    id: string;
    title: string | null;
    description: string | null;
    access_count: number;
    max_access_count: number | null;
    expires_at: string | null;
    created_at: string;
    lead_table_id?: string;
    table: {
      id: string;
      name: string;
      description: string | null;
      created_at: string;
      prospects_analyzed: number | null;
      table_type?: string;
      user_prompt?: string;
      provider_metadata?: {
        criteria?: string[];
      };
    };
  };
  leads: any[];
  columns: string[];
}

interface ErrorState {
  type: 'not_found' | 'expired' | 'limit_exceeded' | 'network_error';
  message: string;
}

// Use shared sidebar color tokens from tokens.ts
const colors = sidebarColors;

// Typewriter text component for character-by-character reveal
const TypewriterText: React.FC<{
  text: string;
  startDelay?: number;
  speed?: number;
  onComplete?: () => void;
  className?: string;
  highlightWords?: { word: string; className: string }[];
}> = ({ text, startDelay = 0, speed = 25, onComplete, className = '', highlightWords = [] }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const startTimer = setTimeout(() => {
      setStarted(true);
    }, startDelay);
    return () => clearTimeout(startTimer);
  }, [startDelay]);

  useEffect(() => {
    if (!started) return;

    let currentIndex = 0;
    const interval = setInterval(() => {
      if (currentIndex <= text.length) {
        setDisplayedText(text.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
        onComplete?.();
      }
    }, speed);

    return () => clearInterval(interval);
  }, [started, text, speed, onComplete]);

  // Apply highlighting to specific words (with XSS and regex injection protection)
  const renderText = () => {
    if (highlightWords.length === 0) return displayedText;

    // Escape HTML in the text first to prevent XSS
    let result = escapeHtml(displayedText);

    highlightWords.forEach(({ word, className: hlClass }) => {
      // Escape regex special characters and HTML in the word
      const escapedWord = escapeRegex(escapeHtml(word));
      if (!escapedWord) return;

      const regex = new RegExp(`(${escapedWord})`, 'gi');
      // hlClass is from our code (not user input), but escape it as well to be safe
      const safeClass = escapeHtml(hlClass);
      result = result.replace(regex, `<strong class="${safeClass}">$1</strong>`);
    });
    return <span dangerouslySetInnerHTML={{ __html: result }} />;
  };

  return (
    <span className={className}>
      {renderText()}
    </span>
  );
};

export const PublicDatasetView: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [sharedData, setSharedData] = useState<SharedDataset | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ErrorState | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(true);
  const professionalTableRef = useRef<any>(null);
  const [showPromo, setShowPromo] = useState(false);

  // Auto-open the promo modal after 5 seconds if not already dismissed
  useEffect(() => {
    if (isDatasetPromoDismissed()) return;
    const timer = setTimeout(() => {
      setShowPromo(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const loadSharedDataset = async () => {
      if (!shareToken) {
        setError({ type: 'not_found', message: 'Invalid share link' });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const result = await apiClient.getSharedDataset(shareToken);

        if (!result) {
          setError({
            type: 'not_found',
            message: 'This shared dataset could not be found, has expired, or has reached its view limit.'
          });
        } else {
          if (!result.dataset || result.leads === undefined) {
            setError({
              type: 'network_error',
              message: 'The shared dataset data is incomplete. Please contact the dataset owner.'
            });
            return;
          }

          if (result.dataset && !result.dataset.table) {
            result.dataset.table = {
              id: result.dataset.lead_table_id,
              name: result.dataset.title || 'Shared Dataset',
              created_at: result.dataset.created_at,
              prospects_analyzed: null
            };
          }

          setSharedData(result);
        }
      } catch (err: any) {
        console.error('Error loading shared dataset:', err);
        setError({
          type: 'network_error',
          message: 'Failed to load the shared dataset. Please try again later.'
        });
      } finally {
        setLoading(false);
      }
    };

    loadSharedDataset();
  }, [shareToken]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const handleSignUp = () => {
    setShowPromo(true);
  };

  const renderError = () => (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center bg-white rounded-2xl p-8 shadow-sm">
        <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h1 className="text-lg font-semibold text-gray-900 mb-2">Dataset Not Found</h1>
        <p className="text-sm text-gray-600 mb-6">{error?.message}</p>
        <button
          onClick={() => window.open(getAppUrl(), '_blank')}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Go to Skylarq
        </button>
      </div>
    </div>
  );

  const renderLoading = () => (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
        <p className="text-sm text-gray-500">Loading dataset...</p>
      </div>
    </div>
  );

  if (loading) return renderLoading();
  if (error || !sharedData) return renderError();

  const { dataset, leads } = sharedData;
  if (!dataset?.table) return renderError();

  const displayTitle = dataset.title || dataset.table.name || 'Shared Dataset';
  const tableType = dataset.table.table_type || 'companies';
  const TypeIcon = tableType === 'people' ? Users : Building2;

  // Get user prompt and criteria for chat display
  const userPrompt = dataset.table.user_prompt || dataset.description || displayTitle;
  const criteria = dataset.table.provider_metadata?.criteria || [];

  // Chat-style action suggestions with colors
  const actionSuggestions = [
    {
      id: 'export',
      icon: Download,
      label: 'Export dataset',
      description: 'CSV or Excel format',
      hoverBg: 'hover:bg-blue-50',
      hoverIconColor: 'group-hover:text-blue-600',
    },
    {
      id: 'campaign',
      icon: Mail,
      label: 'Email campaign',
      description: `${leads.length} contacts ready`,
      hoverBg: 'hover:bg-purple-50',
      hoverIconColor: 'group-hover:text-purple-600',
    },
    {
      id: 'monitor',
      icon: Radio,
      label: 'Auto-monitor',
      description: 'Weekly new matches',
      hoverBg: 'hover:bg-emerald-50',
      hoverIconColor: 'group-hover:text-emerald-600',
    },
    {
      id: 'enrich',
      icon: Zap,
      label: 'Enrich data',
      description: 'Emails, phones & more',
      hoverBg: 'hover:bg-amber-50',
      hoverIconColor: 'group-hover:text-amber-600',
    },
  ];

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Promo Modal — shown on first visit */}
      {showPromo && sharedData && (
        <DatasetPromoModal
          leadCount={leads.length}
          onDismiss={() => setShowPromo(false)}
        />
      )}

      {/* Left Sidebar - Matching AppSidebar design */}
      <div
        className="hidden md:flex flex-col w-16 flex-shrink-0"
        style={{
          backgroundColor: colors.bg.sidebar,
          borderRight: `1px solid ${colors.border.light}`,
        }}
      >
        {/* Header with logo */}
        <div
          className="flex items-center justify-center py-4"
          style={{ borderBottom: `1px solid ${colors.border.lighter}` }}
        >
          <a
            href={getAppUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="transition-opacity hover:opacity-70"
          >
            <img src={skylarqLogo} alt="Skylarq" className="w-8 h-8 object-contain" />
          </a>
        </div>

        {/* Dataset info icon */}
        <div className="flex-1 flex flex-col items-center pt-3 px-2">
          <div
            className="p-2.5 rounded-lg"
            style={{ backgroundColor: colors.bg.selected }}
            title={displayTitle}
          >
            <TypeIcon
              style={{ width: '20px', height: '20px', color: colors.text.primary }}
              strokeWidth={1.5}
            />
          </div>
        </div>

      </div>

      {/* Main Content - Table area */}
      <div className="flex-1 flex flex-col bg-white min-w-0">
        {/* Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[15px] font-semibold text-gray-900 truncate">{displayTitle}</h1>
            <span className="text-[12px] text-gray-400 font-medium flex-shrink-0">{leads.length} results</span>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Share button */}
            <button
              onClick={handleSignUp}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              title="Share dataset"
            >
              <Share2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Share</span>
            </button>

            {/* Monitor button */}
            <button
              onClick={handleSignUp}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              title="Set up monitoring"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Monitor</span>
            </button>

            {/* Mobile CTA */}
            <button
              onClick={handleSignUp}
              className="md:hidden flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white rounded-lg text-[12px] font-medium ml-1"
            >
              Try Free
            </button>
          </div>
        </div>

        {/* Warning Banner */}
        {(dataset.expires_at || dataset.max_access_count) && (
          <div className="px-5 py-2 bg-amber-50 border-b border-amber-100 flex items-center gap-2 flex-shrink-0">
            <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
            <p className="text-[11px] text-amber-800">
              {dataset.expires_at && `Expires ${formatDate(dataset.expires_at)}. `}
              {dataset.max_access_count && `${dataset.max_access_count - dataset.access_count} views remaining.`}
            </p>
          </div>
        )}

        {/* Table - Takes remaining space */}
        <div className="flex-1 min-h-0">
          <ProfessionalTable
            ref={professionalTableRef}
            leads={leads}
            tableId={dataset.table?.id || dataset.lead_table_id || ''}
            onDataRefresh={() => {}}
            onAddColumn={() => {}}
            onRowClick={() => {}}
            isPublicView={true}
            isChatOpen={isChatOpen}
          />
        </div>

        {/* CTA Banner */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-gray-600" />
            <p className="text-[13px] text-gray-700">
              <span className="font-medium">Build your own datasets</span>
              <span className="text-gray-500 ml-1.5">with AI-powered lead generation</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSignUp}
              className="px-3 py-1.5 text-[12px] font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              Sign in
            </button>
            <button
              onClick={handleSignUp}
              className="flex items-center gap-1.5 px-4 py-1.5 bg-gray-900 text-white rounded-lg text-[12px] font-medium hover:bg-gray-800 transition-colors"
            >
              Get Started Free
            </button>
          </div>
        </div>
      </div>

      {/* Right Panel - Compact chat-style interface */}
      {isChatOpen ? (
        <div
          className="hidden md:flex flex-col w-[320px] flex-shrink-0"
          style={{
            borderLeft: `1px solid ${colors.border.light}`,
            backgroundColor: colors.bg.white,
          }}
        >
        {/* Chat header - aligned with table header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <img src={skylarqLogo} alt="Skylarq" className="w-7 h-7 object-contain" />
            <h3 className="text-[15px] font-semibold text-gray-900">Skylarq</h3>
          </div>
          <button
            onClick={() => setIsChatOpen(false)}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Close chat"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Chat messages area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* User's original request - appears instantly */}
          <div className="flex justify-end">
            <div className="bg-gray-900 text-white rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[85%]">
              <p className="text-[14px]">{userPrompt}</p>
            </div>
          </div>

          {/* Typing indicator 1 */}
          <div className="flex gap-2.5 typing-indicator" style={{ animationDelay: '400ms' }}>
            <img src={skylarqLogo} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
            <div className="flex items-center gap-1 px-3 py-2 bg-gray-100 rounded-2xl">
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
            </div>
          </div>

          {/* Skylarq confirms criteria */}
          <div className="flex gap-2.5 chat-message-appear" style={{ animationDelay: '1400ms' }}>
            <img src={skylarqLogo} alt="" className="w-6 h-6 object-contain flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">SKYLARQ</p>
              <p className="text-[14px] text-gray-800 leading-relaxed mb-2">
                <TypewriterText
                  text={`I'll search for ${tableType === 'people' ? 'people' : 'companies'} matching:`}
                  startDelay={1500}
                  speed={25}
                />
              </p>
              {criteria.length > 0 ? (
                <ul className="space-y-1 mb-2">
                  {criteria.slice(0, 4).map((criterion, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-[13px] text-gray-600 criteria-item" style={{ animationDelay: `${2500 + idx * 200}ms` }}>
                      <span className="text-gray-400 mt-0.5">•</span>
                      <span>{criterion}</span>
                    </li>
                  ))}
                  {criteria.length > 4 && (
                    <li className="text-[12px] text-gray-400 ml-4 criteria-item" style={{ animationDelay: `${2500 + 4 * 200}ms` }}>+{criteria.length - 4} more criteria</li>
                  )}
                </ul>
              ) : (
                <p className="text-[13px] text-gray-600 mb-2">Your specified criteria</p>
              )}
            </div>
          </div>

          {/* User confirms - appears after criteria shown */}
          <div className="flex justify-end chat-message-appear" style={{ animationDelay: '3200ms' }}>
            <div className="bg-gray-900 text-white rounded-2xl rounded-br-sm px-4 py-2.5">
              <p className="text-[14px]">Yes, find them</p>
            </div>
          </div>

          {/* Typing indicator 2 - longer for "searching" */}
          <div className="flex gap-2.5 typing-indicator-delayed" style={{ animationDelay: '3600ms' }}>
            <img src={skylarqLogo} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
            <div className="flex items-center gap-1 px-3 py-2 bg-gray-100 rounded-2xl">
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
              <span className="typing-dot"></span>
            </div>
          </div>

          {/* Skylarq shows results */}
          <div className="flex gap-2.5 chat-message-appear" style={{ animationDelay: '5200ms' }}>
            <img src={skylarqLogo} alt="" className="w-6 h-6 object-contain flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">SKYLARQ</p>
              <p className="text-[14px] text-gray-800 leading-relaxed">
                <TypewriterText
                  text={`Found ${leads.length} ${tableType === 'people' ? 'people' : 'companies'} matching your criteria. Click on each match to see the reasoning and sources behind why they were included.`}
                  startDelay={5300}
                  speed={20}
                  highlightWords={[{ word: `${leads.length} ${tableType === 'people' ? 'people' : 'companies'}`, className: 'font-semibold' }]}
                />
              </p>
            </div>
          </div>

          {/* Skylarq suggests next steps */}
          <div className="flex gap-2.5 chat-message-appear" style={{ animationDelay: '8500ms' }}>
            <img src={skylarqLogo} alt="" className="w-6 h-6 object-contain flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">SKYLARQ</p>
              <p className="text-[14px] text-gray-800 leading-relaxed">
                <TypewriterText
                  text="Here's what you can do next with this dataset:"
                  startDelay={8600}
                  speed={25}
                />
              </p>
            </div>
          </div>

          {/* Compact action list */}
          <div className="ml-8 flex flex-col gap-1.5 chat-message-appear" style={{ animationDelay: '10200ms' }}>
            {actionSuggestions.map((action) => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  onClick={handleSignUp}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gray-50/60 text-left transition-all ${action.hoverBg} group`}
                >
                  <div className="w-8 h-8 rounded-lg bg-gray-100 group-hover:bg-white flex items-center justify-center flex-shrink-0 transition-all">
                    <Icon className={`w-4 h-4 text-gray-500 ${action.hoverIconColor} transition-colors`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-gray-900 leading-tight">{action.label}</p>
                    <p className="text-[11px] text-gray-500 truncate">{action.description}</p>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-400 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </div>

        {/* Animation styles */}
        <style>{`
          /* Message appear animation */
          @keyframes messageAppear {
            0% { opacity: 0; transform: translateY(8px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          .chat-message-appear {
            opacity: 0;
            animation: messageAppear 300ms ease-out forwards;
          }

          /* Typing indicator */
          .typing-indicator {
            opacity: 0;
            animation: messageAppear 200ms ease-out forwards, hideTyping 0ms 1300ms forwards;
          }
          .typing-indicator-delayed {
            opacity: 0;
            animation: messageAppear 200ms ease-out forwards, hideTyping 0ms 5100ms forwards;
          }
          @keyframes hideTyping {
            to { opacity: 0; height: 0; margin: 0; padding: 0; overflow: hidden; }
          }

          /* Typing dots */
          .typing-dot {
            width: 6px;
            height: 6px;
            background: #9CA3AF;
            border-radius: 50%;
            animation: typingBounce 1.4s infinite ease-in-out both;
          }
          .typing-dot:nth-child(1) { animation-delay: 0ms; }
          .typing-dot:nth-child(2) { animation-delay: 160ms; }
          .typing-dot:nth-child(3) { animation-delay: 320ms; }
          @keyframes typingBounce {
            0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
            40% { transform: scale(1); opacity: 1; }
          }

          /* Criteria items fade in */
          .criteria-item {
            opacity: 0;
            animation: criteriaFadeIn 300ms ease-out forwards;
          }
          @keyframes criteriaFadeIn {
            0% { opacity: 0; transform: translateX(-8px); }
            100% { opacity: 1; transform: translateX(0); }
          }
        `}</style>

        {/* Chat input - matching platform design */}
        <div className="px-4 py-3 border-t border-gray-200 bg-white">
          {/* Quick Action Pills */}
          <div className="flex gap-2 mb-3">
            <button
              onClick={handleSignUp}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-gray-500 bg-gray-100 rounded-full hover:bg-gray-200 transition-all"
            >
              <Zap className="w-3.5 h-3.5" />
              Find more
            </button>
            <button
              onClick={handleSignUp}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-gray-500 bg-gray-100 rounded-full hover:bg-gray-200 transition-all"
            >
              <Radio className="w-3.5 h-3.5" />
              Monitor
            </button>
            <button
              onClick={handleSignUp}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium text-gray-500 bg-gray-100 rounded-full hover:bg-gray-200 transition-all"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Enrich
            </button>
          </div>

          {/* Input with embedded send button */}
          <div className="relative bg-gray-50 border border-gray-200 rounded-2xl">
            <div className="w-full px-4 py-3 pr-12 text-[14px] text-gray-400 cursor-not-allowed" style={{ minHeight: '44px' }}>
              Ask Skylarq anything...
            </div>
            <button
              onClick={handleSignUp}
              className="absolute right-2 bottom-2 w-8 h-8 bg-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-400 transition-colors"
              title="Sign up to chat"
            >
              <Lock className="w-3.5 h-3.5 text-white" />
            </button>
          </div>
        </div>
        </div>
      ) : (
        /* Collapsed chat - floating pill button (positioned above bottom CTA bar) */
        <button
          onClick={() => setIsChatOpen(true)}
          className="hidden md:flex fixed right-6 items-center gap-2.5 px-4 py-3 bg-gray-900 text-white rounded-full shadow-lg hover:bg-gray-800 transition-all hover:scale-105 z-50"
          style={{ bottom: '70px' }}
          title="Open chat"
        >
          <img src={skylarqLogo} alt="Skylarq" className="w-5 h-5 object-contain" />
          <span className="text-[13px] font-medium">Chat with Skylarq</span>
        </button>
      )}
    </div>
  );
};
